import type { PanelSalesInquiryDetailDto, ReferralCodeStatus } from '@icebreaker/shared';
import type { Translator } from '@icebreaker/shared/i18n';
import type { TagVariant } from '@icebreaker/ui';

/**
 * SATIŞ ADAYINDAKİ REFERANS KODU (Story 3 · P6r). Aday detayındaki "Referans
 * kodu" satırı ve "Kredi yükle" penceresinin hediye bloğu AYNI etiketi ve
 * AYNI gerekçe cümlesini okur: ikisi ayrı yazılsaydı personel iki yüzeyde iki
 * farklı durum görürdü.
 *
 * Gerekçe cümlesinin sayıları ve tarihleri adayın DTO alanlarından gelir
 * (`referralCodeStartsAt` · `EndsAt` · `UsageCount` · `UsageCap` · `UsedAt`).
 * Alan boşsa cümle sayısız biçimine düşer, tahmin yazılmaz.
 */
export type LeadReferral = Pick<
  PanelSalesInquiryDetailDto,
  | 'id'
  | 'referralCode'
  | 'referralCodeStatus'
  | 'referralCodeUsedAt'
  | 'referralCodeGiftCredits'
  | 'referralCodeStartsAt'
  | 'referralCodeEndsAt'
  | 'referralCodeUsageCount'
  | 'referralCodeUsageCap'
>;

/** Durum → rozet tonu (P6r `tone` tablosu, P7 listesiyle aynı renkler). */
export const REFERRAL_LEAD_STATUS_VARIANT: Readonly<Record<ReferralCodeStatus, TagVariant>> = {
  valid: 'success',
  not_found: 'danger',
  inactive: 'neutral',
  not_started: 'accent',
  expired: 'outline',
  cap_reached: 'warning',
  already_used: 'warning',
  no_organization: 'neutral',
};

/** P6r tarih dili: `1 Mar 2027`, `3 Eki 2026`. Gün İstanbul takvimine göre okunur. */
function dayText(t: Translator, iso: string): string {
  return t.formatDate(iso, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/Istanbul',
  });
}

const R = 'admin.sales.detail.referral' as const;

/** Rozet metni; "Bu kurum kullandı" kullanım günüyle yazılır. */
export function referralStatusLabel(
  t: Translator,
  status: ReferralCodeStatus,
  usedAt: string | null,
): string {
  if (status === 'already_used' && usedAt) {
    return t.t(`${R}.usedOn`, { date: dayText(t, usedAt) });
  }
  return t.t(`${R}.statuses.${status}`);
}

/** Geçersiz kodun gerekçe cümlesi (P6r `why`); `valid` için `null`. */
export function referralStatusReason(
  t: Translator,
  lead: LeadReferral,
  organizationName: string,
): string | null {
  const status = lead.referralCodeStatus;
  switch (status) {
    case null:
    case 'valid':
      return null;
    case 'not_started':
      return lead.referralCodeStartsAt
        ? t.t(`${R}.reasons.notStartedOn`, { date: dayText(t, lead.referralCodeStartsAt) })
        : t.t(`${R}.reasons.not_started`);
    case 'expired':
      return lead.referralCodeEndsAt
        ? t.t(`${R}.reasons.expiredOn`, { date: dayText(t, lead.referralCodeEndsAt) })
        : t.t(`${R}.reasons.expired`);
    case 'cap_reached':
      return lead.referralCodeUsageCount !== null && lead.referralCodeUsageCap !== null
        ? t.t(`${R}.reasons.capOf`, {
            used: t.formatNumber(lead.referralCodeUsageCount),
            cap: t.formatNumber(lead.referralCodeUsageCap),
          })
        : t.t(`${R}.reasons.cap_reached`);
    case 'already_used':
      return lead.referralCodeUsedAt
        ? t.t(`${R}.reasons.usedOn`, {
            org: organizationName,
            date: dayText(t, lead.referralCodeUsedAt),
          })
        : t.t(`${R}.reasons.already_used`, { org: organizationName });
    default:
      return t.t(`${R}.reasons.${status}`);
  }
}
